<template>
  <div>
    <StepRegistrationLocation
      v-if="signup && currentSignupStep === 'location'"
      :country-code="user.countryCode"
      :zip-code="user.zipCode"
      @nextStep="nextSignupStep"
      @prevStep="handlePrevStep"
      @attributeChange="handleAttributeChange"
    />
    <StepRegistrationName
      v-if="signup && currentSignupStep === 'name'"
      :first-name="user.firstName"
      :last-name="user.lastName"
      @nextStep="nextSignupStep"
      @prevStep="prevSignupStep"
      @attributeChange="handleAttributeChange"
    />

    <div v-if="login || (signup && currentSignupStep === 'account')">
      <div class="fast-modal-content">
        <Teleport to="#fast-title">
          {{ signup ? 'Vos identifiants de connexion' : 'Connexion' }}
        </Teleport>

        <p v-if="errors.base" class="error text-base mb-4 font-medium text-center text-danger">
          {{ errors.base }}
        </p>

        <StepRegistrationInput
          label="Adresse email"
          :value="user.email"
          :errors="errors.email"
          autofocus
          autocomplete="email"
          name="email"
          @attributeChange="handleAttributeChange('email', $event)"
        />
        <StepRegistrationInput
          label="Mot de passe"
          type="password"
          :value="user.password"
          :errors="errors.password"
          autocomplete="new-password"
          name="password"
          @attributeChange="handleAttributeChange('password', $event)"
        />

        <div v-if="signup" class="form-group" :class="{ 'form-group-invalid': errors.termsOfService }">
          <div class="form-check">
            <input
              id="user_terms_of_service"
              v-model="user.termsOfService"
              :class="{ 'is-invalid': errors.termsOfService }"
              type="checkbox"
              name="user[terms_of_service]"
              aria-required="true"
              required="required"
              class="form-check-input"
              @change="handleAttributeChange('terms_of_service', $event.target.checked)"
            />
            <label for="user_terms_of_service" class="form-check-label font-normal text-gray">
              J'accepte les
              <a href="/pages/terms/general" target="_blank" class="text-gray underline">CGU</a>
              et la
              <a href="/pages/politique-de-confidentialite" target="_blank" class="text-gray underline">
                politique de confidentialité
              </a>
            </label>
          </div>
          <span v-if="errors.termsOfService" class="invalid-feedback block text-sm">
            {{ errors.termsOfService[0] }}
          </span>
        </div>
      </div>

      <div class="flex gap-2 max-md:flex-col items-center p-8">
        <button type="button" class="btn font-semibold text-body" @click.prevent="handlePrevStep()">Retour</button>
        <div class="grow"></div>
        <button
          :disabled="loading"
          type="button"
          class="btn text-gray hover:text-gray-dark focus:text-gray-dark max-md:order-last max-md:mt-2"
          @click="switchView"
        >
          {{ signup ? "J'ai déjà un compte" : "Je n'ai pas de compte" }}
        </button>
        <button
          type="button"
          class="btn btn-primary py-4 px-8 max-md:w-full"
          :disabled="submitDisabled"
          @click.prevent="handleSubmit"
        >
          <template v-if="signup">
            Enregistrer
            <span class="hidden sm:inline">et continuer</span>
          </template>
          <template v-else>Connexion</template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'
import camelCase from 'lodash/camelCase'
import pick from 'lodash/pick'
import transform from 'lodash/transform'
import some from 'lodash/some'
import { csrfToken } from '@/utils/csrf'

import StepRegistrationInput from './StepRegistrationInput.vue'
import StepRegistrationLocation from './StepRegistrationLocation.vue'
import StepRegistrationName from './StepRegistrationName.vue'
import stepEvents from '../mixins/stepEvents'

export default {
  name: 'StepRegistration',
  components: {
    StepRegistrationInput,
    StepRegistrationLocation,
    StepRegistrationName
  },
  mixins: [stepEvents],
  data() {
    const signupSteps = ['location', 'name', 'account']
    return {
      loading: false,
      view: 'signup',
      signupSteps,
      currentSignupStep: signupSteps[0],
      user: {
        firstName: '',
        lastName: '',
        countryCode: '',
        zipCode: '',
        email: '',
        password: '',
        termsOfService: false
      },
      errors: {}
    }
  },
  computed: {
    signup() {
      return this.view === 'signup'
    },
    login() {
      return this.view === 'login'
    },
    currentSignupStepIndex() {
      return this.signupSteps.indexOf(this.currentSignupStep)
    },
    nextSignupStepIndex() {
      return this.currentSignupStepIndex + 1
    },
    prevSignupStepIndex() {
      return this.currentSignupStepIndex - 1
    },
    userParams() {
      return mapKeys(pick(this.user, ['email', 'password', 'termsOfService']), (v, k) => {
        return snakeCase(k)
      })
    },
    userContactParams() {
      const params = pick(this.user, ['firstName', 'lastName', 'zipCode', 'countryCode'])

      return mapKeys(params, (v, k) => snakeCase(k))
    },
    params() {
      if (this.view === 'login') {
        return this.userParams
      }

      return Object.assign(this.userParams, {
        role: 'client',
        user_contact_attributes: this.userContactParams
      })
    },
    submitDisabled() {
      const sample = this.view === 'login' ? this.userParams : this.user
      return this.loading || some(sample, (v, k) => v.length < 1)
    }
  },
  methods: {
    handlePrevStep() {
      if (this.view === 'login' || this.currentSignupStepIndex === 0) {
        this.$emit('prevStep')
      } else {
        this.prevSignupStep()
      }
    },
    nextSignupStep() {
      if (this.nextSignupStepIndex >= this.signupSteps.length) {
        return
      }
      this.currentSignupStep = this.signupSteps[this.nextSignupStepIndex]
    },
    prevSignupStep() {
      if (this.prevSignupStepIndex < 0) {
        return
      }
      this.currentSignupStep = this.signupSteps[this.prevSignupStepIndex]
    },
    handleAttributeChange(param, value) {
      this.user[param] = value
    },
    switchView() {
      this.errors = {}
      this.view = this.view === 'signup' ? 'login' : 'signup'
      this.$nextTick(() => document.querySelector('.fast *[autofocus]')?.focus())
    },
    parseErrors(data) {
      return transform(
        data,
        (res, v, k) => {
          const key = camelCase(k.replace('user_contact.', ''))
          res[key] = v
        },
        {}
      )
    },
    async handleSubmit() {
      if (this.loading) {
        return false
      }

      const token = csrfToken()
      this.errors = {}
      this.loading = true

      try {
        const response = await axios.post(
          `/${this.view}`,
          { user: this.params },
          { headers: { 'X-CSRF-Token': token, Accept: 'application/json' } }
        )
        this.$emit('attributeChange', 'userId', response.data.user.id)
        if (this.view === 'login') {
          const contactResponse = await axios.get('/api/current/user_contacts')
          const contactValidity = contactResponse.data.zip_code && contactResponse.data.country_code

          if (!contactValidity) {
            await axios.put(
              '/api/current/user_contacts',
              { user_contact: this.userContactParams },
              { headers: { 'X-CSRF-Token': token, Accept: 'application/json' } }
            )
          }

          this.$emit('attributeChange', 'started_as_guest', false)
        }
        this.$emit('nextStep')
      } catch (error) {
        const data = error.response.data
        if (data) this.errors = data.error ? { base: data.error } : this.parseErrors(data.errors)
      }
      this.loading = false
    }
  }
}
</script>
